<template>
  <div class="flex flex-col gap-4">
    <TabSelector
      :options="tabOptions"
      :modelValue="selectedIndex"
      @update:modelValue="handleTabChange"
    />

    <component
      :key="currentCondKey"
      :is="currentComponent"
      :condition="currentCond"
      :passageKey="passageKey"
      :securityCode="securityCode"
      :resolvedInstanceId="resolvedInstanceId"
      :make="make"
      :verifyData="verifyData"
      @verified="handleVerified"
    />
  </div>
</template>

<script setup lang="ts">
import { reactive, computed, watch, toRefs } from 'vue'
import type { Condition } from '../types'
import i18n from '@/locales'
import { TabSelector } from '@/components'

import SmsVerifier from './SmsVerifier.vue'
import EmailVerifier from './EmailVerifier.vue'
import AppVerifier from './AppVerifier.vue'
import PasskeyVerifier from './PasskeyVerifier.vue'

const t = (i18n.global as any).t

const emit = defineEmits<{ (e: 'resolved', payload: { choice: Condition; ok: boolean }): void }>()
const props = defineProps<{
  conditions: Condition[]
  passageKey: string
  securityCode: string
  make: (c: Condition) => Promise<any>
  prefer?: (list: Condition[]) => Condition | undefined
  verifyData: { email: string; phone: string; loginType: number; unauthorized: boolean }
}>()

const { conditions, securityCode } = toRefs(props)

const componentMap: Record<string, any> = {
  sms_validate: SmsVerifier,
  email_validate: EmailVerifier,
  app_validate: AppVerifier,
  biometric_validate: PasskeyVerifier,
}

function pickDefaultType(list: Condition[]): string {
  const preferred = props.prefer?.(list) || list[0]
  return preferred?.type || list[0]?.type
}

const picked = reactive<{ type: string }>({
  type: pickDefaultType(props.conditions),
})

watch(
  () => props.conditions,
  (list) => {
    if (!Array.isArray(list) || list.length === 0) return
    if (!list.some((c) => c.type === picked.type)) {
      picked.type = pickDefaultType(list)
    }
  },
  { deep: true },
)

const condMap = computed(() => {
  const map = new Map<string, Condition>()
  for (const c of props.conditions) map.set(c.type, c)
  return map
})

// 供 TabSelector 使用的选项（传递 i18n key，组件内部会调用 t）
const tabOptions = computed(() =>
  props.conditions.map((c) => `components.security.mfa.options.${c.type}`),
)

// 当前选中下标
const selectedIndex = computed(() =>
  Math.max(
    0,
    props.conditions.findIndex((c) => c.type === picked.type),
  ),
)

const currentCond = computed<Condition>(() => {
  return condMap.value.get(picked.type) || props.conditions[0]
})

const currentComponent = computed<any>(() => componentMap[currentCond.value.type])

const currentCondKey = computed(() => `${currentCond.value.type}-${props.securityCode}`)

const resolvedInstanceId = computed(() => `${props.securityCode}-${currentCond.value.type}`)

function handleTabChange(index: number) {
  const target = props.conditions[index]
  if (target?.type) picked.type = target.type
}

function handleVerified(ok: boolean) {
  emit('resolved', { choice: currentCond.value, ok: !!ok })
}
</script>
